import React from 'react';
import { Alert, LinearProgress } from '@mui/material';
import { useHistory, useLocation } from 'react-router';
import _ from 'lodash';

import { usePayrollPayGroupsQuery, usePayrollRunsFlatFileListQuery } from 'generated/graphql';
import { PayrollHeader, PayrollHeaderV1 } from 'containers/PayrollGenericPage/PayrollHeader';
import { PayrollRuns, PayrollRunsV1 } from 'containers/PayrollFlatFilePage/PayrollRuns';

import { URL_PARAM_PAYGROUP, URL_PARAM_PAYROLLRUN } from './consts';
import { PaygroupDropdown } from './PaygroupDropdown';

type Props = {
  layout: (layoutProps: {
    children: React.ReactNode;
    rightActions: React.ReactNode;
  }) => JSX.Element;
};

export const PayrollFlatFilePage = () => {
  const location = useLocation();
  const history = useHistory();
  const paygroupIdParam = Object.fromEntries(new URLSearchParams(location.search))[
    URL_PARAM_PAYGROUP
  ];
  const paygroupsQuery = usePayrollPayGroupsQuery();
  const payrollPayGroupId = paygroupIdParam || paygroupsQuery.data?.payrollPayGroups[0]?.id;
  const payGroupsCount = paygroupsQuery.data?.payrollPayGroups.length;

  const payrollRunsQuery = usePayrollRunsFlatFileListQuery({
    // @ts-expect-error Apollo still think we call this with `payrollPayGroupId` undefined
    // even though the `skip` should prevent that
    variables: { payrollPayGroupId },
    skip: !payrollPayGroupId,
  });

  return (
    <div>
      <PayrollHeader />
      {paygroupsQuery.loading && <LinearProgress />}
      {paygroupsQuery.error && <Alert severity="error">The paygroups could not be fetched.</Alert>}
      {payGroupsCount === 0 && (
        <Alert severity="warning">Your organization does not have pay groups.</Alert>
      )}
      {(payGroupsCount || 0) > 1 && paygroupsQuery.data?.payrollPayGroups && (
        <div style={{ margin: '30px 0px' }}>
          <PaygroupDropdown
            payrollPayGroups={paygroupsQuery.data.payrollPayGroups}
            payrollPayGroupId={payrollPayGroupId}
            onChange={(value) => {
              const urlSearchParams = new URLSearchParams(location.search);
              urlSearchParams.set(URL_PARAM_PAYGROUP, value);
              urlSearchParams.delete(URL_PARAM_PAYROLLRUN);
              history.replace({
                pathname: location.pathname,
                search: urlSearchParams.toString(),
              });
            }}
          />{' '}
        </div>
      )}
      {payrollPayGroupId && (
        <PayrollRuns
          query={payrollRunsQuery}
          selectedPayGroup={paygroupsQuery.data?.payrollPayGroups.find(
            (p) => p.id === payrollPayGroupId
          )}
          hideLoading={paygroupsQuery.loading}
        />
      )}
    </div>
  );
};

export const PayrollFlatFilePageV1 = ({ layout: Layout }: Props): JSX.Element => {
  const location = useLocation();
  const history = useHistory();
  const paygroupIdParam = Object.fromEntries(new URLSearchParams(location.search))[
    URL_PARAM_PAYGROUP
  ];
  const paygroupsQuery = usePayrollPayGroupsQuery();
  const payrollPayGroupId = paygroupIdParam || paygroupsQuery.data?.payrollPayGroups[0]?.id;
  const payGroupsCount = paygroupsQuery.data?.payrollPayGroups.length;

  const payrollRunsQuery = usePayrollRunsFlatFileListQuery({
    // @ts-expect-error Apollo still think we call this with `payrollPayGroupId` undefined
    // even though the `skip` should prevent that
    variables: { payrollPayGroupId },
    skip: !payrollPayGroupId,
  });

  const processedStats = payrollRunsQuery.data?.payrollRunsFlatFile
    ?.filter((p) => p.runState === 'PROCESSED')
    .map((p) => p.stats);

  const paygroupSelect = (payGroupsCount || 0) > 1 && paygroupsQuery.data?.payrollPayGroups && (
    <PaygroupDropdown
      payrollPayGroups={paygroupsQuery.data.payrollPayGroups}
      payrollPayGroupId={payrollPayGroupId}
      onChange={(value) => {
        const urlSearchParams = new URLSearchParams(location.search);
        urlSearchParams.set(URL_PARAM_PAYGROUP, value);
        urlSearchParams.delete(URL_PARAM_PAYROLLRUN);
        history.replace({
          pathname: location.pathname,
          search: urlSearchParams.toString(),
        });
      }}
    />
  );

  return (
    <Layout rightActions={paygroupSelect}>
      <PayrollHeaderV1
        totalDeduction={
          processedStats && {
            intent: _.sum(processedStats.map((s) => s.totalUsd)),
            actual: _.sum(processedStats.map((s) => s.totalUsdSuccess)),
            currency: 'USD',
          }
        }
      />
      {paygroupsQuery.loading && <LinearProgress />}
      {paygroupsQuery.error && <Alert severity="error">The paygroups could not be fetched.</Alert>}
      {payGroupsCount === 0 && (
        <Alert severity="warning">Your organization does not have pay groups.</Alert>
      )}
      {payrollPayGroupId && (
        <PayrollRunsV1
          query={payrollRunsQuery}
          selectedPayGroup={paygroupsQuery.data?.payrollPayGroups.find(
            (p) => p.id === payrollPayGroupId
          )}
          hideLoading={paygroupsQuery.loading}
        />
      )}
    </Layout>
  );
};
